import { useState, type ReactNode } from "react";
import { Plus, X } from "lucide-react";

/**
 * FaqAccordion — the numbered, +/× accordion list (rounded cards; the open card
 * lifts to white with a blue accent ring). One item open at a time. Content via
 * props; named tokens only.
 *
 * Extracted from the pricing page's local FAQ so the same "system" can frame the
 * pricing questions and the right column of the /faq page. Token fixes vs the
 * original inline version: the faint `/NN`-on-ds-token classes (which render
 * transparent because the ds tokens are `var()` hexes) are replaced with solid
 * tokens — `text-ink-faint` for the index, `ring-ds-accent` for the active ring.
 */

export type FaqItem = { q: string; a: ReactNode };

export function FaqAccordion({
  items,
  defaultOpen = 0,
  numbered = true,
}: {
  items: FaqItem[];
  /** Index open on mount (default the first). Pass -1 for all-collapsed. */
  defaultOpen?: number;
  /** Show the leading index number (default true). */
  numbered?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="flex flex-col gap-3">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div
            key={i}
            className={`rounded-3xl transition-all duration-300 ${isOpen ? "bg-white shadow-[0_18px_44px_-22px_rgba(15,23,42,0.22)] ring-1 ring-ds-accent" : "bg-white ring-1 ring-line"}`}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left md:px-6 md:py-5"
              aria-expanded={isOpen}
            >
              {numbered && <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-bg-subtle text-[13px] font-semibold tabular-nums text-ds-ink-soft">{i + 1}</span>}
              <span className="flex-1 text-ds-18 font-semibold leading-snug text-ds-ink">{item.q}</span>
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors ${isOpen ? "bg-white text-ds-accent ring-1 ring-ds-accent" : "bg-ds-accent text-white"}`}>
                {isOpen ? <X size={16} strokeWidth={2.2} /> : <Plus size={16} strokeWidth={2.2} />}
              </span>
            </button>
            <div className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
              <div className="overflow-hidden">
                <p className={`max-w-[600px] px-5 pb-5 ${numbered ? "pl-14" : ""} text-ds-14 leading-relaxed text-ds-ink-soft md:px-6 md:pb-6`}>{item.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
